import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { colors } from '../../lib/theme';
import { LENS_LABELS } from '../../lib/lens-labels';
import type { FilterToggles } from '../../lib/screening-presets';
import { SkeletonResultsTable } from './SkeletonResultsTable';

const LENS_ORDER: Array<{ key: keyof Pick<FilterToggles, 'defense' | 'shariah'> | 'occupation'; label: string; enabled: (t: FilterToggles) => boolean }> = [
  { key: 'occupation', label: LENS_LABELS.occupation, enabled: (t) => t.bds.enabled },
  { key: 'defense', label: LENS_LABELS.defense, enabled: (t) => t.defense },
  { key: 'shariah', label: LENS_LABELS.shariah, enabled: (t) => t.shariah },
];

export function ScanningView({ toggles }: { toggles: FilterToggles }) {
  const active = LENS_ORDER.filter((l) => l.enabled(toggles));
  const [done, setDone] = useState<string[]>([]);

  useEffect(() => {
    setDone([]);
    const timers = active.map((l, i) =>
      setTimeout(() => setDone((d) => (d.includes(l.key) ? d : [...d, l.key])), 350 + i * 480)
    );
    return () => timers.forEach(clearTimeout);
    // Intentionally keyed off toggle flags; `active` is derived each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggles.bds.enabled, toggles.defense, toggles.shariah]);

  return (
    <div className="py-16 flex flex-col items-center text-center" role="status" aria-live="polite">
      <Loader2 className="h-11 w-11 animate-spin" style={{ color: colors.blue }} />
      <h2 className="display text-[34px] tracking-tight mt-6 mb-1.5 font-normal">Checking your holdings…</h2>
      <p className="text-[14.5px] m-0" style={{ color: colors.textMuted }}>
        Matching companies to curated datasets and rules.
      </p>
      <div className="mt-8 flex flex-col gap-2.5 min-w-[320px] max-w-sm w-full">
        {active.map((l) => {
          const isDone = done.includes(l.key);
          return (
            <div
              key={l.key}
              className="flex items-center justify-between px-4 py-3 rounded-[11px]"
              style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
            >
              <span className="text-[13.5px] font-medium" style={{ color: colors.textSecondary }}>
                {l.label}
              </span>
              {isDone ? (
                <CheckCircle2 className="h-4 w-4" style={{ color: colors.green }} />
              ) : (
                <span className="font-mono text-[11px] scan-pulse" style={{ color: colors.textFaint }}>
                  scanning
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-10 w-full max-w-3xl">
        <SkeletonResultsTable toggles={toggles} />
      </div>
    </div>
  );
}
